import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  ChevronUp,
  ChevronDown,
  Copy,
  Check,
  Search,
  Eye,
  Languages,
} from 'lucide-mui';
import { useTags } from '../../../../hooks/useTags';
import {
  CHAPTERS_TIMESTAMPS_VISIBLE_KEY,
  CHAPTERS_EXPANDED_KEY,
} from '../../../../constants/tags';

interface ChapterToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  isAllExpanded: boolean;
  onExpandAll: () => void;
  onCopyChapters: () => void;
  copySuccess: boolean;
  showVisualElements: boolean;
  onToggleVisualElements: () => void;
  onTranslateClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isTranslating: boolean;
}

const ChapterToolbar: React.FC<ChapterToolbarProps> = ({
  searchTerm,
  onSearchChange,
  isAllExpanded,
  onExpandAll,
  onCopyChapters,
  copySuccess,
  showVisualElements,
  onToggleVisualElements,
  onTranslateClick,
  isTranslating,
}) => {
  const { setTag } = useTags();

  const handleExpandAll = () => {
    onExpandAll();
    setTag({
      key: CHAPTERS_EXPANDED_KEY,
      value: (!isAllExpanded).toString(),
    });
  };

  const handleToggleVisualElements = () => {
    onToggleVisualElements();
    setTag({
      key: CHAPTERS_TIMESTAMPS_VISIBLE_KEY,
      value: (!showVisualElements).toString(),
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        marginBottom: 1,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexGrow: 1,
          backgroundColor: 'background.default',
          borderRadius: '8px',
          height: '40px',
          border: '1px solid',
          borderColor: 'divider',
          '& .MuiOutlinedInput-root': {
            height: '100%',
            '& fieldset': {
              borderColor: 'transparent',
            },
            '&:hover fieldset': {
              borderColor: 'transparent',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'transparent',
            },
            '& .MuiInputAdornment-root': {
              '& .MuiSvgIcon-root': {
                fontSize: '20px',
                color: 'text.secondary',
                transition: 'color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              },
            },
            '&:hover .MuiInputAdornment-root .MuiSvgIcon-root': {
              color: 'text.primary',
            },
          },
        }}
      >
        <TextField
          placeholder="Search chapters..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          variant="outlined"
          fullWidth
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          backgroundColor: 'background.default',
          borderRadius: '8px',
          height: '40px',
          border: '1px solid',
          borderColor: 'divider',
          '& .MuiIconButton-root': {
            width: '28px',
            height: '28px',
            borderRadius: '8px',
            margin: '6px',
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              transform: 'translateY(-1px)',
              '& .MuiSvgIcon-root': {
                transform: 'scale(1.05)',
              },
            },
            '&:active': {
              transform: 'translateY(1px)',
              '& .MuiSvgIcon-root': {
                transform: 'scale(0.98)',
              },
            },
            '& .MuiSvgIcon-root': {
              fontSize: '18px',
              transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            },
          },
        }}
      >
        <Tooltip title="Toggle visual elements" placement="top" arrow>
          <IconButton
            onClick={handleToggleVisualElements}
            sx={{
              color: showVisualElements ? 'primary.main' : 'text.primary',
            }}
          >
            <Eye />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={isAllExpanded ? 'Collapse all' : 'Expand all'}
          placement="top"
          arrow
        >
          <IconButton
            onClick={handleExpandAll}
            sx={{
              color: isAllExpanded ? 'primary.main' : 'text.primary',
            }}
          >
            {isAllExpanded ? <ChevronUp /> : <ChevronDown />}
          </IconButton>
        </Tooltip>
        <Tooltip
          title={copySuccess ? 'Copied!' : 'Copy chapters'}
          placement="top"
          arrow
        >
          <IconButton
            onClick={onCopyChapters}
            sx={{
              color: copySuccess ? 'success.main' : 'text.primary',
            }}
          >
            {copySuccess ? <Check /> : <Copy />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Translate" placement="top" arrow>
          <IconButton
            onClick={onTranslateClick}
            disabled={isTranslating}
            sx={{
              '&.Mui-disabled': {
                opacity: 0.5,
              },
            }}
          >
            <Languages />
          </IconButton>
        </Tooltip>
      </Paper>
    </Box>
  );
};

export default ChapterToolbar;
